import { useSessionManagementServiceV1 } from "@rw1/api-services";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  toActivityMetrics,
  toApiMetricFilter,
} from "../../../../domain/mappers/metricsMappers";
import type {
  ActivityMetrics,
  Dimension,
} from "../../../../domain/models/metrics";
import { GC_TIME_MS, STALE_TIME_MS } from "../../../../hooks/constants";

export const ALL_AGGREGATE_METRICS_QUERY_KEY = "allAggregateMetrics";

interface UseAllAggregateMetricsParams {
  startDate: Date;
  endDate: Date;
  dimension: Dimension;
  contentMetadataIds?: string[];
  countries?: string[];
  minSessionDurationInMinutes?: number;
}

interface UseAllAggregateMetricsResult {
  data: ActivityMetrics | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
}

export const useAllAggregateMetrics = ({
  startDate,
  endDate,
  dimension,
  contentMetadataIds,
  countries,
  minSessionDurationInMinutes,
}: UseAllAggregateMetricsParams): UseAllAggregateMetricsResult => {
  const sessionManagementService = useSessionManagementServiceV1();

  const { companyId } = useParams();

  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: [
      ALL_AGGREGATE_METRICS_QUERY_KEY,
      companyId,
      startDate.toISOString(),
      endDate.toISOString(),
      dimension,
      contentMetadataIds,
      countries,
      minSessionDurationInMinutes,
    ],
    queryFn: async () => {
      if (!companyId) {
        throw new Error("Company ID is required");
      }

      const filter = toApiMetricFilter({
        startDate,
        endDate,
        dimension,
        contentMetadataIds,
        countries,
        minSessionDurationInMinutes,
      });

      const result = await sessionManagementService.getAllAggregateMetrics(
        companyId,
        filter,
      );

      return toActivityMetrics(result);
    },
    enabled: Boolean(companyId),
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    placeholderData: keepPreviousData,
    retry: 1,
  });

  return {
    data,
    isLoading,
    isFetching,
    error: error ?? null,
  };
};
